// ELŐZMÉNY-TIMELINE (pont + összekötő vonal) — a terv `.tl` mintája.
//
// A `docs/foundations/design.md` §9 szerint ez SHARED JELÖLT (az admin jegy- és napló-nézete is
// elkelne vele), de a promóció szabálya a MÁSODIK használat: amíg csak a partner-felület használja, itt él.
// Az áthelyezés `components/shared/`-be mechanikus lesz — a komponens semmit nem tud a partner-doménről.
import type { ComponentType, ReactNode, SVGProps } from 'react';

import { Text } from '@/components/ui/text';
import { cn } from '@/lib/utils';

export interface TimelineItem {
  id: string;
  label: string;
  /** Másodlagos sor (időpont · szereplő). */
  meta?: string | undefined;
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
  /** A pont tónusa — a semleges az alap, a `brand`/`success` a kiemelt esemény. */
  tone?: 'neutral' | 'brand' | 'success' | undefined;
}

export function PortalTimeline({ items }: { items: readonly TimelineItem[] }): JSX.Element {
  return (
    <ol className="flex flex-col pl-1">
      {items.map(({ id, label, meta, Icon, tone = 'neutral' }, i) => (
        <li
          key={id}
          className={cn(
            'relative flex gap-3 pb-4 last:pb-0',
            // ÖSSZEKÖTŐ VONAL: a pont alatt indul és a következő elemig fut — az utolsónál nincs.
            i < items.length - 1 &&
              'before:bg-border before:absolute before:bottom-0 before:left-[11px] before:top-[26px] before:w-[1.5px] before:content-[""]',
          )}
        >
          <span
            className={cn(
              'z-10 flex size-[23px] shrink-0 items-center justify-center rounded-full border [&>svg]:size-3',
              tone === 'brand' && 'bg-brand-soft text-brand border-transparent',
              tone === 'success' && 'bg-success-soft text-success border-transparent',
              tone === 'neutral' && 'bg-muted text-muted-foreground',
            )}
            aria-hidden="true"
          >
            <Icon />
          </span>
          <span className="flex min-w-0 flex-col gap-0.5 pt-0.5">
            <Text variant="itemTitle" as="span">
              {label}
            </Text>
            {meta !== undefined && (
              <Text variant="secondary" as="span" className="text-[11px]">
                {meta}
              </Text>
            )}
          </span>
        </li>
      ))}
    </ol>
  );
}

/** Kártya-fejléc alatti, keret nélküli blokk-cím (a terv `card-h` + `card-b` párosának egyszerű alakja). */
export function PortalCardBody({ children }: { children: ReactNode }): JSX.Element {
  return <div className="p-4">{children}</div>;
}
